import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useGentleMotion as useReducedMotion } from '../hooks/useGentleMotion';
import { useIdleReady } from '../hooks/useIdleReady';
const COLORS = ['#F6C453', '#F27C70', '#6CBBD5', '#2D8C5A', '#F3A340', '#B39DDB'];
type Piece = {
  left: number;
  size: number;
  color: number;
  duration: number;
  delay: number;
  drift: number;
  round: boolean;
};
interface ConfettiRainProps {
  /** How many pieces fall continuously on desktop. */
  count?: number;
  /** Opacity of the whole layer. */
  opacity?: number;
}

/**
 * Continuous, slow confetti fall. The layer always sits BEHIND the content
 * (z-0 vs. z-10 content) and uses fewer pieces on small screens so it never
 * competes with the copy on mobile.
 */
export function ConfettiRain({
  count = 18,
  opacity = 0.75
}: ConfettiRainProps) {
  const reduce = useReducedMotion();
  const idle = useIdleReady();
  const pieces = useMemo<Piece[]>(() => Array.from({
    length: count
  }).map((_, i) => ({
    left: i * 97 % 100,
    size: 6 + i * 5 % 7,
    color: i % COLORS.length,
    duration: 9 + i * 3 % 7,
    delay: i % 10 * 1.1,
    drift: i % 2 === 0 ? 26 : -26,
    round: i % 3 === 0
  })), [count]);

  // Decorative only: mounted after the browser is idle so it never delays paint.
  if (reduce || !idle) return null;
  return <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" style={{
    opacity
  }} aria-hidden="true" data-id="element-58" data-mp="8urv5nm20yc4">
      {pieces.map((p, i) => <motion.span key={i}
    /* keep the mobile density low so text always reads cleanly */ className={i % 2 === 1 ? 'absolute top-0 hidden sm:block' : 'absolute top-0'} style={{
      left: `${p.left}%`,
      width: p.round ? p.size : p.size * 1.6,
      height: p.size,
      borderRadius: p.round ? 999 : 3,
      backgroundColor: COLORS[p.color]
    }} initial={{
      y: '-10%',
      x: 0,
      rotate: 0,
      opacity: 0
    }} animate={{
      y: '110vh',
      x: [0, p.drift, 0, -p.drift, 0],
      rotate: 360,
      opacity: [0, 1, 1, 0.9, 0]
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-59" data-mp="ik1mv2czkezp" data-mpi={i} />)}
    </div>;
}